import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useState, useContext } from 'react';
import { useSiteContext } from '@zooplus/commons-lang';
import { InfoCircle, ErrorCircle, PawMid, CheckCircle } from '@zooplus/icons';
import { LayoutContainer, InverseButton, DropdownMenu, PrimaryButton, DropdownMenuCheckboxList, Input, FormMessage } from '@zooplus/ui-components';
import { postToSubscribeToNewsletter } from '../../services/newsletterSubscriptionService.js';
import { useErrorHandler } from '../ErrorContext.js';
import styles from './Newsletter.module.scss.js';
import NewsletterConfirmModal from './NewsletterConfirmModal.js';
import PageWidthContext from '../../contexts/PageWidthContext.js';

const Newsletter = ({ footerNewsletterBanner }) => {
    const [petSelection, setPetSelection] = useState([]);
    const [email, setEmail] = useState('');
    const [emailStatus, setEmailStatus] = useState('unknown');
    const [alreadySubscribed, setAlreadySubscribed] = useState(false);
    const [generalError, setGeneralError] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [confirmationText, setConfirmationText] = useState(footerNewsletterBanner.confirmationText);
    const [showGPDR, setShowGPDR] = useState(false);
    const { locale } = useSiteContext();
    const errorHandler = useErrorHandler();
    const pageWidth = useContext(PageWidthContext);
    const subscribeToNewsletter = async () => {
        try {
            await postToSubscribeToNewsletter(errorHandler, locale, {
                email: email,
                selectedTopics: petSelection.reduce((acc, value) => ({ ...acc, [value]: true }), {}),
            });
            let modifiedText = footerNewsletterBanner.confirmationText.replace('{userEmail}', email);
            setConfirmationText(modifiedText);
            setShowModal(true);
            setGeneralError(false);
        }
        catch (e) {
            const error = e;
            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        setAlreadySubscribed(true);
                        setGeneralError(false);
                        break;
                    case 422:
                        setEmailStatus('invalid');
                        setGeneralError(false);
                        break;
                    default:
                        setGeneralError(true);
                }
            }
            else {
                setGeneralError(true);
            }
        }
    };
    const handleToggleCheckPet = (pet) => {
        const index = petSelection.indexOf(pet);
        if (index === -1) {
            setPetSelection([...petSelection, pet]);
        }
        else {
            const newSelection = [...petSelection];
            newSelection.splice(index, 1);
            setPetSelection(newSelection);
        }
    };
    const handleSetEmail = (email) => {
        setEmail(email);
        setEmailStatus(email
            ?.toLowerCase()
            .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
            ? 'valid'
            : 'invalid');
    };
    const getErrorMessage = () => {
        if (emailStatus === 'invalid') {
            return footerNewsletterBanner.emailErrorMessage;
        }
        if (alreadySubscribed) {
            return footerNewsletterBanner.alreadySubscribedErrorMessage;
        }
        return undefined;
    };
    const isSendButtonActive = petSelection.length > 0 && emailStatus === 'valid';
    return (jsxs("section", { "data-zta": "footer-newsletter", className: styles.shopFooterNewsletter, children: [jsx("div", { className: styles.shopFooterNewsletterWrapper, children: jsx(LayoutContainer, { variant: pageWidth, children: jsxs("div", { className: styles.newsletterBackground, children: [jsx(NewsletterDiscount, { bonusPointsNumber: footerNewsletterBanner.bonusPointsNumber, bonusPointsText: footerNewsletterBanner.bonusPointsText, newsletterHeadline: footerNewsletterBanner.newsletterHeadline }), jsxs("div", { className: styles.formSection, children: [jsx("div", { className: classNames(styles.newsletterHeadline, styles.onlyDesktop), children: footerNewsletterBanner.newsletterHeadline }), jsxs("div", { className: styles.formSectionBottom, children: [jsx(NewsletterPetSelection, { pets: footerNewsletterBanner.petList, placeholder: footerNewsletterBanner.petListPlaceholder, selection: petSelection, buttonLabel: footerNewsletterBanner.petSelectionButtonText, onTogglePet: handleToggleCheckPet }), jsx(NewsletterEmailInput, { value: email, isValid: emailStatus !== 'invalid' && !alreadySubscribed, errorMessage: getErrorMessage(), placeholder: footerNewsletterBanner.emailInputPlaceholder, onChange: handleSetEmail, onFocus: () => setShowGPDR(true), onBlur: () => setShowGPDR(false) })] })] }), jsx("div", { className: styles.subscribeButton, children: jsx(InverseButton, { "data-zta": "subscription-button", variant: "secondary", disabled: !isSendButtonActive, size: "normal", onClick: () => {
                                        subscribeToNewsletter();
                                    }, children: footerNewsletterBanner.subscriptionButtonText }) })] }) }) }), showGPDR && (jsx("div", { className: styles.gpdrMessageBackground, children: jsx(LayoutContainer, { "data-zta": "gpdr-message", className: classNames(styles.gpdrMessageWrapper, pageWidth === 'default' && styles.forceMaxWidth), variant: pageWidth, children: jsxs("div", { className: styles.gpdrMessage, children: [jsx("div", { children: jsx(InfoCircle, {}) }), jsx("div", { dangerouslySetInnerHTML: { __html: footerNewsletterBanner.disclaimerText } })] }) }) })), generalError && (jsx(LayoutContainer, { "data-zta": "general-error", className: classNames(styles.generalErrorWrapper, pageWidth === 'default' && styles.forceMaxWidth), variant: pageWidth, children: jsxs("div", { className: styles.generalErrorMessage, children: [jsx("div", { children: jsx(ErrorCircle, {}) }), jsx("div", { dangerouslySetInnerHTML: {
                                __html: footerNewsletterBanner.genericSubscriptionErrorMessage,
                            } })] }) })), jsx(NewsletterConfirmModal, { show: showModal, onClose: () => setShowModal(false), confirmationHeadline: footerNewsletterBanner.confirmationHeadline, confirmationText: confirmationText, confirmationButtonText: footerNewsletterBanner.confirmationButtonText })] }));
};
const NewsletterDiscount = ({ bonusPointsNumber, bonusPointsText, newsletterHeadline, }) => {
    return (jsxs("div", { className: styles.discountWrapper, children: [jsxs("div", { className: styles.circleWrapper, children: [jsxs("div", { className: styles.amountAndText, children: [jsx("span", { children: bonusPointsNumber }), jsx("span", { children: bonusPointsText })] }), jsx("div", { className: styles.paw, children: jsx(PawMid, {}) })] }), jsx("div", { className: classNames(styles.newsletterHeadline, styles.onlyMobile), children: jsx("span", { children: newsletterHeadline }) })] }));
};
const NewsletterPetSelection = ({ pets, placeholder, selection, buttonLabel, onTogglePet, }) => {
    const [show, setShow] = useState(false);
    const handleToggleShow = () => {
        setShow(!show);
    };
    return (jsx("div", { className: styles.petSelection, "data-zta": "footer-pets-selection-menu", children: jsx(DropdownMenu, { menuId: "footer-pets-selection-menu", onOpen: handleToggleShow, onClose: handleToggleShow, isOpen: show, zIndex: 10000, breakpoint: "md", triggerLabel: jsxs(Fragment, { children: [!!selection.length && (jsx("span", { children: pets
                            .filter(({ pet }) => selection.includes(pet))
                            .map(({ petName }) => petName)
                            .join(',') })), !selection.length && jsx("span", { children: placeholder })] }), dropdownButton: jsx(PrimaryButton, { "data-zta": "pet-checkbox-confirm-btn", length: "full", onClick: handleToggleShow, children: buttonLabel }), children: jsx(DropdownMenuCheckboxList, { items: pets.map(({ pet, petName }) => ({
                    id: pet,
                    isChecked: selection.includes(pet),
                    label: petName,
                    name: pet,
                    value: pet,
                })), onItemUpdate: ({ id }) => onTogglePet(id) }) }) }));
};
const NewsletterEmailInput = ({ value, isValid, errorMessage, placeholder, onChange, onFocus, onBlur, }) => {
    const handleChange = (e) => {
        const value = e.target.value;
        onChange(value);
    };
    return (jsxs("div", { className: styles.emailInputWrapper, children: [jsx(Input, { className: styles.emailInput, onChange: handleChange, hasError: !isValid, onFocus: onFocus, onBlur: (event) => {
                    if (!event.relatedTarget) {
                        onBlur();
                    }
                }, inputProps: {
                    placeholder,
                    'data-zta': 'newsletter-email-input',
                }, icon: isValid && value.length ? jsx(CheckCircle, {}) : undefined }), !isValid && (jsx(FormMessage, { hasError: true, infoIcon: "info", className: styles.emailValidation, children: errorMessage }))] }));
};

export { Newsletter as default };
//# sourceMappingURL=Newsletter.js.map
